// client/src/pages/RegisterPage.js
import React, { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext'; // Import useAuth hook

function RegisterPage() {
  const { register, loading: authLoading } = useAuth(); // Access register function and loading state from context
  const navigate = useNavigate(); // Hook to navigate programmatically

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    companyName: '',
    companyAddress: '',
    companyPhone: '',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false); // Local loading state for form submission
  const [passwordStrength, setPasswordStrength] = useState(null); // Password strength feedback

  // Debounce password strength check
  const checkPasswordStrength = useCallback(async (password, email, username) => {
    if (!password) {
      setPasswordStrength(null);
      return;
    }

    try {
      const response = await fetch('/api/auth/check-password-strength', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, email, username })
      });

      if (!response.ok) {
        console.error('Password strength check failed:', response.status);
        setPasswordStrength(null);
        return;
      }

      const data = await response.json();
      setPasswordStrength(data);
    } catch (err) {
      console.error('Error checking password strength:', err);
      setPasswordStrength(null);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Check password strength as user types
    if (name === 'password') {
      checkPasswordStrength(value, formData.email, formData.username);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Clear previous errors
    setLoading(true); // Set local loading state

    try {
      // Call the register function from AuthContext
      await register(formData);
      navigate('/dashboard'); // Redirect to dashboard on successful registration
    } catch (err) {
      // Display error message from the backend or a generic one
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false); // Reset local loading state
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4 sm:p-6">
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200 w-full max-w-md">
<h2 className="text-3xl font-bold text-primary-700 mb-6 text-center">Register for SwiftBidPro</h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Your unique username"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              required
            />

            {/* Password Strength Meter */}
            {passwordStrength && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Password Strength</span>
                  <span className={`text-xs font-semibold ${
                    passwordStrength.score === 0 ? 'text-red-600' :
                    passwordStrength.score === 1 ? 'text-orange-600' :
                    passwordStrength.score === 2 ? 'text-yellow-600' :
                    passwordStrength.score === 3 ? 'text-blue-600' :
                    'text-green-600'
                  }`}>
                    {passwordStrength.score === 0 ? 'Very Weak' :
                     passwordStrength.score === 1 ? 'Weak' :
                     passwordStrength.score === 2 ? 'Fair' :
                     passwordStrength.score === 3 ? 'Good' :
                     'Strong'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      passwordStrength.score === 0 ? 'bg-red-600 w-1/5' :
                      passwordStrength.score === 1 ? 'bg-orange-600 w-2/5' :
                      passwordStrength.score === 2 ? 'bg-yellow-600 w-3/5' :
                      passwordStrength.score === 3 ? 'bg-blue-600 w-4/5' :
                      'bg-green-600 w-full'
                    }`}
                  />
                </div>
                {passwordStrength.feedback.warning && (
                  <p className="mt-2 text-xs text-gray-600">{passwordStrength.feedback.warning}</p>
                )}
                {passwordStrength.feedback.suggestions && passwordStrength.feedback.suggestions.length > 0 && (
                  <ul className="mt-2 text-xs text-gray-600 list-disc list-inside">
                    {passwordStrength.feedback.suggestions.map((suggestion, idx) => (
                      <li key={idx}>{suggestion}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="pt-4 border-t border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Company Details (Optional)</h3>
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Your Company Inc."
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="companyAddress" className="block text-sm font-medium text-gray-700 mb-1">Company Address</label>
              <input
                type="text"
                id="companyAddress"
                name="companyAddress"
                value={formData.companyAddress}
                onChange={handleChange}
                placeholder="123 Business Rd"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="companyPhone" className="block text-sm font-medium text-gray-700 mb-1">Company Phone</label>
              <input
                type="tel" // Use type="tel" for phone numbers
                id="companyPhone"
                name="companyPhone"
                value={formData.companyPhone}
                onChange={handleChange}
                placeholder="555-123-4567"
                className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>
          
          <button
            type="submit"
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-300"
            disabled={loading || authLoading} // Disable button when submitting or AuthContext is loading
          >
            {loading || authLoading ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Register'
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-gray-600 text-sm">
          By registering, you agree to our{' '}
          <Link to="/privacy-policy" className="font-medium text-primary-600 hover:text-primary-500">
            Privacy Policy
          </Link>
          .
        </p>
        <p className="mt-2 text-center text-gray-600 text-sm">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
            Login here
          </Link>
        </p>
      </div>
    </div>
  );
}

export default RegisterPage;
